import {
  navigateToSalesPortalRoute,
  ROUTE_REQUIRED_PARAMS,
} from '@/experiences/sales-portal/sales-portal-route-config';
import {
  NavigationProp,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import { SALES_PORTAL_ROUTES } from '@/navigators/routes';
import {
  SalesHubSidebarNavItem,
  useSalesHubSidebarNavItemsLoader,
} from '@/experiences/sales-portal/containers/sidebar/blocs/loaders/use-sales-hub-sidebar-nav-items-loader';
import {
  SalesPortalStackParams,
  SalesPortalTabParams,
} from '@/navigators/types';
import { SidenavItem } from '@/domain/navigation/types';
import { useCallback, useMemo } from 'react';
import { usePricingSidebarNavItems } from '@/experiences/sales-portal/domain/pricing/blocs/hooks/use-pricing-sidebar-nav-items';

interface UseSalesPortalSidebarNavResult {
  loading: boolean;
  navItems: SidenavItem[];
}

/**
 * Loads the sales-portal sidebar nav items and wires their press handlers
 * with cross-tab-aware navigation. Shared by `DesktopWithSidebarTemplate`,
 * `SalesHubShellTemplate`, and `SalesPortalTabBar` so the param-picking +
 * nested-navigate logic stays in one place.
 *
 * Static-route items use `navigateToSalesPortalRoute` (handles lazy-loaded
 * tabs); dynamic microproduct items navigate to `Base` with their `productId`.
 */
export const useSalesPortalSidebarNav = (): UseSalesPortalSidebarNavResult => {
  const route = useRoute();
  const navigation = useNavigation<NavigationProp<SalesPortalTabParams>>();
  const { navItems: loaderItems, loading } = useSalesHubSidebarNavItemsLoader();
  const pricingNavItems = usePricingSidebarNavItems();

  const handleNavItemPress = useCallback(
    (item: SalesHubSidebarNavItem) => {
      if (item.route) {
        // Pick only the required params for the target route to avoid leaking
        // unrelated params (e.g. action, productId).
        const currentParams = (route.params ?? {}) as Record<string, unknown>;
        const requiredKeys = ROUTE_REQUIRED_PARAMS[item.route] ?? [];
        const pickedParams: Record<string, unknown> = {};
        for (const key of requiredKeys) {
          if (currentParams[key] !== undefined) {
            pickedParams[key] = currentParams[key];
          }
        }
        navigateToSalesPortalRoute(navigation, item.route, {
          ...pickedParams,
          ...item.params,
        } as SalesPortalStackParams[keyof SalesPortalStackParams]);
      } else {
        // Dynamic microproduct route — navigate to Base. Drop only `mpRoute`
        // (the previous product's in-product route) so switching products
        // doesn't seed the newly selected product's iframe with a foreign
        // route; other params carry as before.
        const nextParams = {
          ...((route.params ?? {}) as Record<string, unknown>),
        };
        delete nextParams.mpRoute;
        navigation.navigate(SALES_PORTAL_ROUTES.Base, {
          ...nextParams,
          productId: item.id,
        } as SalesPortalTabParams[typeof SALES_PORTAL_ROUTES.Base]);
      }
    },
    [navigation, route.params],
  );

  const navItems = useMemo<SidenavItem[]>(
    () => [
      ...loaderItems.map((item) => ({
        active: item.isActive,
        icon: item.icon,
        iconUrl: item.iconUrl,
        id: item.id,
        label: item.label,
        onPress: () => handleNavItemPress(item),
      })),
      ...pricingNavItems,
    ],
    [loaderItems, handleNavItemPress, pricingNavItems],
  );

  return { loading, navItems };
};
